/** Milk eligibility is checked here for UX; Shopify local delivery validates the checkout destination. */
class PremiumMilkDelivery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.input = this.querySelector('[data-milk-pincode]');
    this.status = this.querySelector('[data-milk-status]');
    this.toast = this.querySelector('[data-milk-toast]');
    this.toastMessage = this.toast.querySelector('[data-milk-toast-message]');
    this.allowed = new Set((this.dataset.pincodes || '').split(/[\s,;]+/).filter((pin) => /^[1-9]\d{5}$/.test(pin)));
    this.variants = new Set((this.dataset.variants || '').split(','));
    this.validatedPincode = '';
    this.querySelector('[data-milk-check]').addEventListener('click', () => this.validate(), { signal });
    this.querySelector('[data-milk-toast-close]').addEventListener('click', () => { this.toast.hidden = true; }, { signal });
    // Keep the toast outside glass/transform containers so fixed placement follows the viewport.
    document.body.append(this.toast);
    this.input.addEventListener('input', () => this.reset(), { signal });
    this.input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.validate();
    }, { signal });
    // Capture before theme product-form and add-to-cart handlers, including the sticky form.
    document.addEventListener('click', (event) => this.handlePurchase(event), { capture: true, signal });
    document.addEventListener('submit', (event) => this.handlePurchase(event), { capture: true, signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    clearTimeout(this.toastTimer);
    if (this.toast) { this.toast.hidden = true; this.append(this.toast); }
  }

  reset() {
    this.validatedPincode = '';
    this.removeAttribute('data-valid');
    this.removeAttribute('data-error');
    this.input.removeAttribute('aria-invalid');
    this.status.textContent = this.dataset.help;
    this.toast.hidden = true;
    this.syncForms('');
  }

  validate(showPopup = false) {
    const pin = this.input.value.trim();
    const valid = /^[1-9]\d{5}$/.test(pin) && this.allowed.has(pin);
    this.validatedPincode = valid ? pin : '';
    this.toggleAttribute('data-valid', valid);
    this.toggleAttribute('data-error', !valid);
    this.input.setAttribute('aria-invalid', String(!valid));
    const message = valid ? this.dataset.success.replace('[pincode]', pin)
      : !pin ? this.dataset.required
      : !/^[1-9]\d{5}$/.test(pin) ? this.dataset.invalid : this.dataset.unavailable;
    this.status.textContent = message;
    this.syncForms(valid ? pin : '');
    if (!valid && showPopup) {
      this.toastMessage.textContent = message;
      this.toast.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { this.toast.hidden = true; }, 9000);
      this.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      this.input.focus({ preventScroll: true });
    }
    return valid;
  }

  isMilkForm(form) {
    return form instanceof HTMLFormElement && this.variants.has(form.querySelector('[name="id"]')?.value);
  }

  syncForms(pin) {
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (!this.isMilkForm(form)) return;
      let property = form.querySelector('[name="properties[Delivery pincode]"]');
      if (!property) {
        property = document.createElement('input');
        property.type = 'hidden';
        property.name = 'properties[Delivery pincode]';
        form.append(property);
      }
      property.value = pin;
      property.disabled = !pin;
    });
  }

  handlePurchase(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const button = target.closest('button');
    const form = event.type === 'submit' ? target : button?.form;
    if (!this.isMilkForm(form)) return;
    if (event.type === 'click' && (!button || (button.type !== 'submit' && !button.hasAttribute('data-milk-buy-now')))) return;
    if (!this.validate(true)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (button?.hasAttribute('data-milk-buy-now')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.buyNow(form, button);
    }
  }

  async buyNow(form, button) {
    if (this.buying || !form.reportValidity()) return;
    this.buying = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const label = button.textContent;
    button.textContent = this.dataset.processing;
    try {
      const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.dataset.checkoutError);
      const checkout = new URL(this.dataset.checkoutUrl, window.location.origin);
      checkout.searchParams.set('checkout[shipping_address][zip]', this.validatedPincode);
      checkout.searchParams.set('checkout[shipping_address][city]', 'Vadodara');
      checkout.searchParams.set('checkout[shipping_address][country]', 'India');
      checkout.searchParams.set('checkout[shipping_address][province]', 'Gujarat');
      window.location.assign(checkout.href);
    } catch (error) {
      this.status.textContent = error.message || this.dataset.checkoutError;
      this.setAttribute('data-error', '');
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = label;
      this.buying = false;
    }
  }
}

if (!customElements.get('premium-milk-delivery')) customElements.define('premium-milk-delivery', PremiumMilkDelivery);
